import { type ExperienceActivationPayload } from '../../../../../types/models'
import { type EmbedElementContext } from '../../../../element-embedder'
import { type DirectLayoutModeConfig, type DirectEmbedStrategySettings } from '../../settings'
import { type IDirectRenderer } from '../direct-renderer'
import { type IVueActivationTargetRenderer } from './targets/activation-target-renderer'
import { VueActivationTargetBottomRenderer } from './targets/bottom'
import { type Ref, ref, computed, type ComputedRef } from 'vue-demi'
import { type UseElementBoundingReturn, type UseWindowSizeReturn, useWindowSize, refThrottled } from '@vueuse/core'
import { VueActivationTargetOverlayRenderer } from './targets/overlay'
import { useDynamicListLayout, useCurrentLayoutMode, useCurrentLayout, type LayoutTargetConfig } from '@source-digital/use-layout-mode-vue'
import { DefaultActivationDetailsRenderer } from './details/details'
import './vue-direct-renderer.css'
import { mount } from './utils/mount-debug-component'
import { type IDetailsOverlayTarget } from './targets/details-overlay-target'
import { useElementBoundingPatch as useElementBounding } from './utils/patches/use-element-bounding-patch'
import * as presets from './presets'
import { useQuasarV1Shim } from '@source-digital/quasar-v1-shim'
import { merge } from 'lodash-es'
import { VueActivationTargetOuterOverlayRenderer } from './targets/outer-overlay'
import { applyInlineStyles } from '../../../../../utils/dom-style'
import { type IAnalytics } from '../../analytics/analytics'

export class VueDirectRenderer implements IDirectRenderer {
  ctx: EmbedElementContext
  currentItems: Ref<ExperienceActivationPayload[]> = ref([])
  directSettings: DirectEmbedStrategySettings
  hidden: Ref<boolean>
  targetRendererMap = new Map<string, IVueActivationTargetRenderer>()
  printPartition: () => void
  currentMode: Ref<string | undefined>
  videoSizeRef: UseElementBoundingReturn
  windowSizeRef: UseWindowSizeReturn
  currentLayout: Ref<DirectLayoutModeConfig>
  detailsRenderer: DefaultActivationDetailsRenderer
  #debugComponent: any
  detailsTargetMap: Map<string, IDetailsOverlayTarget | undefined>

  constructor (ctx: EmbedElementContext, public readonly analytics: IAnalytics) {
    this.ctx = ctx
    this.hidden = ref(false)
    this.directSettings = this.#loadSettings()
    // looks like vueuse attempts to clean up on scope dispose,
    // but I'm not sure if it applies to this case
    this.videoSizeRef = useElementBounding(this.ctx.targetEl)
    this.windowSizeRef = useWindowSize()
    const { currentMode } = useCurrentLayoutMode(this.directSettings.layout?.breakpoints, query => {
      if (typeof query === 'boolean') return query
      if ('maxMediaWidth' in query) {
        return computed(() => query.maxMediaWidth === -1 || this.videoSizeRef.width.value <= query.maxMediaWidth)
      }
      return undefined
    })
    this.currentMode = currentMode
    const { currentLayout } = useCurrentLayout(currentMode, this.directSettings.layout?.modes ?? {})
    const throttleMs = this.directSettings.options?.layoutThrottleMs ?? 100
    this.currentLayout = throttleMs > 0 ? refThrottled(currentLayout, throttleMs) : currentLayout
    this.detailsTargetMap = new Map()
    if (this.ctx.isDebugEnabled('vueDirectRenderer')) {
      this.mountDebugComponent()
    }
  }

  #loadSettings (): DirectEmbedStrategySettings {
    if (this.ctx.settings.direct == null) return presets.standard
    const layoutPreset = this.ctx.settings.direct.useLayoutPreset
    if (layoutPreset == null) return this.ctx.settings.direct
    const preset = presets[layoutPreset]
    if (preset == null) {
      throw new Error(`Invalid preset name: ${layoutPreset}. Valid presets are: ${Object.keys(presets).join(', ')}`)
    }
    return merge({}, presets[layoutPreset], this.ctx.settings.direct)
  }

  mountDebugComponent () {
    if (this.#debugComponent == null) {
      this.#debugComponent = mount(this)
    }
    this.#debugComponent.setOpen(true)
  }

  async initialize (): Promise<void> {
    // enable by default
    const { readyPromise, quasarWrapperClass } = useQuasarV1Shim(this.ctx.settings.direct?.options?.enableQuasarV1Shim !== false)
    await readyPromise
    // Limitation: this lazy import does not work when bundled as iife by /browser module.
    if (this.directSettings.options?.disableAnimateCss !== true) await import('@source-digital/embed-animate.css/dist/embed-animate.compat.css')
    const initOptions = this.directSettings.layout?.initOptions
    const enabledTargets = ['bottom', 'overlay', 'outerOverlay'].filter(i => initOptions?.[i]?.disabled !== true)
    const layoutTargetConfigMap: ComputedRef<Record<string, LayoutTargetConfig>> = computed(() => {
      return enabledTargets.reduce((acc, key) => {
        acc[key] = this.currentLayout.value?.targets?.[key] ?? {}
        return acc
      }, {})
    })
    const dynamicListLayoutRefs = useDynamicListLayout(
      this.currentLayout,
      this.currentItems,
      (item) => item.activation?.instance?.position,
      layoutTargetConfigMap,
      {
        fallbackToAvailablePosition: computed(() => this.currentLayout.value.fallbackToAvailablePosition ?? true)
      }
    )
    const createElement = (...args: Parameters<typeof this.ctx.createElement>): ReturnType<typeof this.ctx.createElement> => {
      const element = this.ctx.createElement(...args)
      if (this.directSettings.options?.disableAnimateCss !== true) element.classList.add('sd-embed-component--animated')
      return element
    }
    this.detailsRenderer = new DefaultActivationDetailsRenderer(this.ctx, this.currentLayout, this.windowSizeRef, this.videoSizeRef, this.detailsTargetMap, quasarWrapperClass, dynamicListLayoutRefs.getItemPositionAndTarget, createElement, this.hidden, this.analytics)

    this.printPartition = dynamicListLayoutRefs.printPartition
    if (initOptions?.bottom?.disabled !== true) {
      const { items } = dynamicListLayoutRefs.getItemsRefByTarget('bottom')
      const renderer = new VueActivationTargetBottomRenderer(items, this.directSettings, this.ctx, this.currentLayout, this.windowSizeRef, this.videoSizeRef, this.detailsRenderer, quasarWrapperClass, createElement, this.hidden, this.analytics)
      await renderer.initialize()
      this.targetRendererMap.set('bottom', renderer)
      this.detailsTargetMap.set('bottom', renderer)
    }
    if (initOptions?.overlay?.disabled !== true) {
      const { itemsMap } = dynamicListLayoutRefs.getItemsMapRefByTarget('overlay')
      const renderer = new VueActivationTargetOverlayRenderer(itemsMap, this.directSettings, this.ctx, this.currentLayout, this.windowSizeRef, this.videoSizeRef, this.detailsRenderer, quasarWrapperClass, createElement, this.hidden, this.analytics)
      await renderer.initialize()
      this.targetRendererMap.set('overlay', renderer)
      this.detailsTargetMap.set('overlay', renderer)
    }
    if (initOptions?.outerOverlay?.enabled === true) {
      const { itemsMap } = dynamicListLayoutRefs.getItemsMapRefByTarget('outerOverlay')
      const renderer = new VueActivationTargetOuterOverlayRenderer(itemsMap, this.directSettings, this.ctx, this.currentLayout, this.windowSizeRef, this.videoSizeRef, this.detailsRenderer, quasarWrapperClass, createElement, this.hidden, this.analytics)
      await renderer.initialize()

      const overlayRenderer = this.targetRendererMap.get('overlay') as VueActivationTargetOverlayRenderer | undefined

      renderer.onVideoAreaResize((size, style) => {
        if (style == null) return
        const mediaEl = this.ctx.targetEl
        applyInlineStyles(style, mediaEl)
        if (overlayRenderer != null) {
          overlayRenderer.applyStyleToWrapper(style)
        }
      })
      this.targetRendererMap.set('outerOverlay', renderer)
      this.detailsTargetMap.set('outerOverlay', renderer)
    }
  }

  get targets (): IVueActivationTargetRenderer[] {
    return Array.from(this.targetRendererMap.values())
  }

  get currentActivations (): ExperienceActivationPayload[] {
    return this.currentItems.value
  }

  setCurrentActivations (items: ExperienceActivationPayload[]): void {
    this.currentItems.value = items
  }

  destroy (): void {
    this.targets.forEach(target => { target?.destroy?.() })
  }

  hide (): void {
    this.hidden.value = true
  }

  show (): void {
    this.hidden.value = false
  }
}
